import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [fp, setFp] = useState({ username: "", recovery_email: "", new_password: "" });
  const [fpLoading, setFpLoading] = useState(false);
  const [fpShow, setFpShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      login(data.token, data.user);
      toast.success(`Selamat datang, ${data.user.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async () => {
    if (!fp.username || !fp.recovery_email || !fp.new_password) return toast.error("Lengkapi semua kolom");
    setFpLoading(true);
    try {
      await api.post("/auth/forgot-password", fp);
      toast.success("Password admin berhasil direset. Silakan login dengan password baru.");
      setForgotOpen(false);
      setFp({ username: "", recovery_email: "", new_password: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#3368A0]/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-teal-500/20 blur-3xl" />
      <div className="w-full max-w-sm relative z-10">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo className="mb-4 h-20 w-20 drop-shadow-lg" testId="login-brand-logo" />
          <h1 className="text-2xl font-extrabold text-white tracking-tight">MLC Attendance</h1>
          <p className="text-sm text-slate-400 mt-1">Absensi & Payroll Karyawan Laundry</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username">Username</Label>
            <Input id="username" data-testid="login-username-input" value={username}
              onChange={(e) => setUsername(e.target.value)} placeholder="Masukkan username" autoComplete="username" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input id="password" data-testid="login-password-input" type={show ? "text" : "password"}
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Masukkan password"
                className="pr-10" autoComplete="current-password" required />
              <button type="button" data-testid="toggle-password-visibility-button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
                {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <Button type="submit" data-testid="login-submit-button" disabled={loading}
            className="w-full bg-[#3368A0] hover:bg-[#284f7a] rounded-xl h-11 font-semibold">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Masuk"}
          </Button>

          <div className="pt-1 text-center">
            <button type="button" data-testid="forgot-password-link"
              onClick={() => setForgotOpen(true)}
              className="text-sm text-[#3368A0] hover:text-[#284f7a] font-medium transition-colors">
              Lupa Password?
            </button>
          </div>
        </form>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-white" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#3368A0]" /> Lupa Password Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="rounded-xl bg-[#F2EFE7] p-3 text-xs text-slate-600">
              Fitur ini hanya untuk akun <b>admin</b>. Masukkan username admin dan email pemulihan yang terdaftar untuk mengatur password baru.
              <br />Karyawan: silakan hubungi <b>management via WhatsApp</b> untuk reset password.
            </div>
            <div className="space-y-1.5">
              <Label>Username Admin</Label>
              <Input data-testid="forgot-username-input" value={fp.username}
                onChange={(e) => setFp({ ...fp, username: e.target.value })} placeholder="admin" />
            </div>
            <div className="space-y-1.5">
              <Label>Email Pemulihan</Label>
              <Input type="email" data-testid="forgot-email-input" value={fp.recovery_email}
                onChange={(e) => setFp({ ...fp, recovery_email: e.target.value })} placeholder="email@perusahaan.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Password Baru</Label>
              <div className="relative">
                <Input type={fpShow ? "text" : "password"} data-testid="forgot-new-password-input" value={fp.new_password}
                  onChange={(e) => setFp({ ...fp, new_password: e.target.value })} placeholder="Minimal 6 karakter" className="pr-10" />
                <button type="button" onClick={() => setFpShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {fpShow ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)}>Batal</Button>
            <Button data-testid="forgot-submit-button" onClick={submitForgot} disabled={fpLoading} className="bg-[#3368A0] hover:bg-[#284f7a]">
              {fpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
